
import { css } from '@emotion/react';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import 'ol/ol.css';
import 'ol-ext/dist/ol-ext.css';

import { useSessionStore } from 'session/useSessionStore';
import {useStartMap} from '../hooks/useStartMap';
import {useUpdateLayer} from '../hooks/useUpdateLayer';
import {useSeismicStore} from '../stores/useSeismicStore';
import {TraceHeaderTable} from './TraceHeaderTable';
import { useGetGeometryProjections } from './projections/hooks/useGetGeometryProjections';
import { GeopostSnackbar } from 'components/geopostSnackbar/GeopostSnackbar';
import { SeismicKeyboard } from './keyboard/components/SeismicKeyboard';
import { useMapSignalR } from '../hooks/useMapSignalR';
import { useMapStore } from '../stores/useMapStore';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { VolumeDebugPanel } from './VolumeDebugPanel';
import { useFeatureFlags } from '../hooks/useFeatureFlags';

const mapStyle = css({
    width: '100%',
    flexGrow: 1,
});

const loadingStyle = css({
    position: 'absolute',
    background: 'rgb(0,0,0,.9)',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10
});

export function SeismicPanel() {
    const { colorbar, isLoading,showTraceHeaderTable, warningMessage, setWarningMessage } = useSeismicStore(state => ({
        colorbar: state.colorbar,
        isLoading: state.isLoading,
        showTraceHeaderTable: state.showTraceHeaderTable,
        warningMessage: state.warningMessage,
        setWarningMessage: state.setWarningMessage,
    }));

    const featureFlags = useFeatureFlags();
    const backgroundColor = useMapStore(state => state.backgroundColor);

    const warningMessageRef = useRef('');

    const showWarningMsg = useMemo(() => !!warningMessage, [warningMessage]);

    useGetGeometryProjections();

    const ref = useRef<HTMLDivElement>(null);

    const seismic2DDefaults = useSessionStore(state => state.tenantConfiguration?.viewer2DDefaults);

    const { error } = useStartMap(ref);

    // eslint-disable-next-line no-restricted-globals
    const [ showFullScreenAlert, setShowFullScreenAlert ] = useState(screen.availWidth <= 400);

    const { current: showDebugLayer } = useRef(getParamsFromUrl().showRestrictedArea);
    useUpdateLayer();
    //useInterpretations();
    //useMousePointerGeomUpdate(geometryLine);

    useEffect(() => {
        window.addEventListener('resize', () => {
            // eslint-disable-next-line no-restricted-globals
            setShowFullScreenAlert(screen.availWidth !== window.innerWidth);
        });
    }, []);

    useEffect(() => {
        if (!!warningMessage) {
            setTimeout(() => setWarningMessage(null), 5000);
        }
    }, [warningMessage]);

    return (
        <Fragment>
            <div id='map' css={mapStyle} ref={ref} style={{backgroundColor: backgroundColor}}></div>
            {isLoading && <div css={loadingStyle}>
                <CircularProgress size={60} style={{ color: '#fff' }} />
            </div>}
            {showTraceHeaderTable &&<TraceHeaderTable></TraceHeaderTable>}

            {
                showDebugLayer
                &&
                <VolumeDebugPanel/>
            }

            <GeopostSnackbar />

            <Snackbar open={!!error} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert variant='filled' severity="error">{error}</Alert>
            </Snackbar>
            {
                featureFlags.featureFlags?.seismic2D.ResponsivityWarning
                &&
                <Snackbar open={!!showFullScreenAlert} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}} onClose={() => setShowFullScreenAlert(false)}>
                    <Alert variant='filled' severity="info">For the best experience, please maximize your browser window. Some features may not work properly unless the screen is fully expanded.</Alert>
                </Snackbar>
            }
            <Snackbar open={!!warningMessage} anchorOrigin={{horizontal: 'center', vertical: 'bottom'}}>
                <Alert
                    variant='filled'
                    severity='warning'
                >
                    {warningMessage}
                </Alert>
            </Snackbar>
        </Fragment>
    );
}